import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import { useI18n } from '../../features/i18n';
import { Skeleton, SkeletonRow } from '../shell/Skeleton';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/menu-manager — hand-rebuilt in brand primitives.
 *
 * Focused scope covers the 80% case: browse + edit items (name, price,
 * description, allergens, dietary tags, needs-review toggle), manage
 * specials and upsell tips, edit restaurant profile basics. For advanced
 * features (modifier groups, menu import, quality dashboard, bulk review
 * workflow), /unified/menu-manager-legacy wraps the original themed MUI
 * version as a fallback.
 *
 * Structure:
 *   Hero → restaurant picker → quality overview → tab pills → tab body
 *
 * Source of data + save logic: dataService (same as legacy screen).
 */

const EASE = [0.22, 1, 0.36, 1];

const ALLERGEN_OPTIONS = ['Gluten', 'Dairy', 'Nuts', 'Peanut', 'Shellfish', 'Fish', 'Eggs', 'Soy', 'Sesame'];
const DIETARY_OPTIONS  = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Kosher', 'Halal', 'Keto', 'Paleo'];
const DAYS_OF_WEEK     = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PREP_METHODS     = ['Grilled', 'Fried', 'Baked', 'Roasted', 'Steamed', 'Raw', 'Braised', 'Sautéed', 'Smoked', 'Pan-seared'];

// Labels are resolved at render time via the i18n dict (see `TABS(t)` below).
const TABS_DEF = [
  { id: 'items',    tKey: 'menu_manager.tab_items',    icon: 'menu' },
  { id: 'specials', tKey: 'menu_manager.tab_specials', icon: 'flame' },
  { id: 'upsell',   tKey: 'menu_manager.tab_upsell',   icon: 'lightbulb' },
  { id: 'profile',  tKey: 'menu_manager.tab_profile',  icon: 'store' },
];

export default function UnifiedMenuManager() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const { t } = useI18n();

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(() => localStorage.getItem('mm_selected_restaurant') || '');
  const [restaurant, setRestaurant] = useState(null);
  const [menuData, setMenuData] = useState(null);              // DRAFT — mutated as the operator edits
  const [publishedMenuData, setPublishedMenuData] = useState(null); // LIVE — snapshot at last publish
  const [menuId, setMenuId] = useState(null);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('items');
  const [editItem, setEditItem] = useState(null);            // { categoryId, item, isNew }
  const [editSpecial, setEditSpecial] = useState(null);
  const [editTip, setEditTip] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.listRestaurants();
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : (list?.restaurants || []);
        setRestaurants(arr);
        if (!selectedRestaurantId && arr.length > 0) {
          setSelectedRestaurantId(String(arr[0].id));
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // `selectedRestaurantId` is read only to preserve prior selection
    // across mounts. Re-running the restaurant list fetch on every
    // selection change would cause an infinite loop, so we intentionally
    // omit it from the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isInitialized, navigate]);

  useEffect(() => {
    if (!selectedRestaurantId) { setLoading(false); return; }
    localStorage.setItem('mm_selected_restaurant', String(selectedRestaurantId));
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await dataService.getRestaurant(selectedRestaurantId);
        if (cancelled) return;
        setRestaurant(data);
        if (data?.menus && data.menus.length > 0) {
          const md = data.menus[0].menu_data;
          setMenuData(md);
          // Deep clone for the published snapshot so diffing against the
          // draft doesn't false-positive once the operator mutates items.
          setPublishedMenuData(md ? JSON.parse(JSON.stringify(md)) : null);
          setMenuId(data.menus[0].id);
          if (md?.menus?.length) setSelectedMenuId(md.menus[0].id);
        } else {
          const blank = blankMenuData();
          setMenuData(blank);
          setPublishedMenuData(null); // nothing published yet
          setMenuId(null);
        }
      } catch (e) {
        if (!cancelled) showAlert('red', e?.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantId]);

  const showAlert = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3500);
  };

  const publish = async () => {
    if (!menuData) return;
    setSaving(true);
    try {
      const updated = { ...menuData, updated_at: new Date().toISOString(), published_at: new Date().toISOString() };
      if (menuId !== null) {
        await dataService.updateRestaurantMenu(selectedRestaurantId, menuId, JSON.stringify(updated));
      } else {
        await dataService.addRestaurantMenu(selectedRestaurantId, JSON.stringify(updated));
      }
      setMenuData(updated);
      // After publish, the draft IS now live — snapshot it as the new
      // baseline so subsequent edits diff against this version.
      setPublishedMenuData(JSON.parse(JSON.stringify(updated)));
      setPublishOpen(false);
      showAlert('green', 'Menu published — diners see this now.');
    } catch (e) {
      showAlert('red', 'Failed to publish: ' + (e?.message || 'unknown'));
    } finally {
      setSaving(false);
    }
  };

  // Revert draft back to published snapshot — the escape hatch for
  // accidental edits before they hit diners.
  const revertToPublished = () => {
    if (!publishedMenuData) return;
    if (!window.confirm('Discard all unpublished changes and revert to the live menu?')) return;
    setMenuData(JSON.parse(JSON.stringify(publishedMenuData)));
    showAlert('green', 'Reverted to the live version.');
  };

  const activeMenu = menuData?.menus?.find((m) => m.id === selectedMenuId) || menuData?.menus?.[0];

  // --- Mutations on menuData (all local until save) ---

  const updateActiveMenu = (fn) => {
    if (!menuData) return;
    const next = { ...menuData, menus: (menuData.menus || []).map((m) => m.id === (activeMenu?.id) ? fn(m) : m) };
    setMenuData(next);
  };

  const upsertItem = (categoryId, nextItem) => {
    updateActiveMenu((m) => ({
      ...m,
      categories: (m.categories || []).map((c) => c.id !== categoryId ? c : ({
        ...c,
        items: (() => {
          const items = c.items || [];
          const idx = items.findIndex((i) => i.id === nextItem.id);
          if (idx >= 0) {
            const copy = items.slice(); copy[idx] = nextItem; return copy;
          }
          return [...items, nextItem];
        })(),
      })),
    }));
  };

  const deleteItem = (categoryId, itemId) => {
    updateActiveMenu((m) => ({
      ...m,
      categories: (m.categories || []).map((c) => c.id !== categoryId ? c : ({
        ...c,
        items: (c.items || []).filter((i) => i.id !== itemId),
      })),
    }));
  };

  const addCategory = () => {
    const name = window.prompt('Category name?');
    if (!name) return;
    updateActiveMenu((m) => ({
      ...m,
      categories: [...(m.categories || []), {
        id: uid('cat'), name: name.trim(), display_order: (m.categories?.length || 0) + 1, items: [],
      }],
    }));
  };

  const deleteCategory = (categoryId) => {
    if (!window.confirm('Delete this category and all its items?')) return;
    updateActiveMenu((m) => ({ ...m, categories: (m.categories || []).filter((c) => c.id !== categoryId) }));
  };

  const renameCategory = (categoryId) => {
    const current = activeMenu?.categories?.find((c) => c.id === categoryId)?.name || '';
    const next = window.prompt('Rename category:', current);
    if (!next) return;
    updateActiveMenu((m) => ({ ...m, categories: (m.categories || []).map((c) => c.id === categoryId ? { ...c, name: next.trim() } : c) }));
  };

  // Per-menu serve window (e.g. breakfast 7a–10:30a, dinner 5p–10p) so
  // clients / chat can scope suggestions to what's actually being served now.
  const updateActiveMenuWindow = (patch) => updateActiveMenu((m) => ({ ...m, ...patch }));

  const upsertSpecial = (nextSpecial) => {
    setMenuData((md) => ({
      ...md,
      specials: (() => {
        const arr = md.specials || [];
        const idx = arr.findIndex((s) => s.id === nextSpecial.id);
        if (idx >= 0) { const copy = arr.slice(); copy[idx] = nextSpecial; return copy; }
        return [...arr, nextSpecial];
      })(),
    }));
  };
  const deleteSpecial = (id) => setMenuData((md) => ({ ...md, specials: (md.specials || []).filter((s) => s.id !== id) }));

  const upsertTip = (nextTip) => {
    setMenuData((md) => ({
      ...md,
      upsell_tips: (() => {
        const arr = md.upsell_tips || [];
        const idx = arr.findIndex((t) => t.id === nextTip.id);
        if (idx >= 0) { const copy = arr.slice(); copy[idx] = nextTip; return copy; }
        return [...arr, nextTip];
      })(),
    }));
  };
  const deleteTip = (id) => setMenuData((md) => ({ ...md, upsell_tips: (md.upsell_tips || []).filter((t) => t.id !== id) }));

  // Bulk "mark all reviewed" — category scope or entire active menu
  const markCategoryReviewed = (categoryId) => {
    updateActiveMenu((m) => ({
      ...m,
      categories: (m.categories || []).map((c) => c.id !== categoryId ? c : ({
        ...c,
        items: (c.items || []).map((it) => ({ ...it, needs_review: false })),
      })),
    }));
  };
  const markMenuReviewed = () => {
    updateActiveMenu((m) => ({
      ...m,
      categories: (m.categories || []).map((c) => ({
        ...c,
        items: (c.items || []).map((it) => ({ ...it, needs_review: false })),
      })),
    }));
  };

  // Menu import — parse raw pasted text into categories + items. The parser
  // is intentionally simple and defensive: CATEGORY headings are ALL-CAPS or
  // title-case lines without prices; item lines look like "Name — description
  // — $price" or "Name - $price" or just "Name $price". Lines that match
  // nothing get dropped.
  const importFromText = (rawText) => {
    const parsed = parseMenuText(rawText);
    if (!parsed.categories.length) {
      showAlert('red', "Couldn't parse any items. Try adding a blank line between categories.");
      return false;
    }
    updateActiveMenu((m) => ({
      ...m,
      categories: [
        ...(m.categories || []),
        ...parsed.categories.map((c, i) => ({
          id: uid('cat'),
          name: c.name,
          display_order: (m.categories?.length || 0) + i + 1,
          items: c.items.map((it) => ({
            id: uid('item'),
            name: it.name,
            description: it.description || '',
            price: it.price,
            price_type: 'FIXED',
            allergens: [],
            dietary_tags: [],
            prep_methods: [],
            modifier_groups: [],
            removable_ingredients: [],
            needs_review: true, // imported items default to needs-review
            available: true,
          })),
        })),
      ],
    }));
    setImportOpen(false);
    showAlert('green', `Imported ${parsed.categories.length} categor${parsed.categories.length === 1 ? 'y' : 'ies'}, ${parsed.itemCount} items. Each is flagged "needs review".`);
    return true;
  };
  const toggleTipEnabled = (id) => setMenuData((md) => ({
    ...md,
    upsell_tips: (md.upsell_tips || []).map((t) => t.id === id ? { ...t, enabled: !t.enabled } : t),
  }));

  const updateProfile = async (form) => {
    try {
      // Serialize the extended form shape into what the backend accepts:
      // hours → hours_json (string), cover_photo → photo_url (string),
      // amenities → amenities (array).
      const payload = {
        name:         form.name,
        address:      form.address,
        phone:        form.phone,
        cuisine_type: form.cuisine_type,
        description:  form.description,
        website:      form.website,
        amenities:    Array.isArray(form.amenities) ? form.amenities : [],
        hours_json:   form.hours ? JSON.stringify(form.hours) : null,
        photo_url:    form.cover_photo?.dataUrl || null,
      };
      const updated = await dataService.updateRestaurant(selectedRestaurantId, payload, userId);
      // Backend may return partial shape; preserve our optimistic fields.
      setRestaurant({ ...(restaurant || {}), ...(updated || {}), ...payload });
      showAlert('green', 'Profile saved.');
    } catch (e) {
      showAlert('red', 'Failed to save profile.');
    }
  };

  const quality = useMemo(() => computeQuality(menuData), [menuData]);
  const diff = useMemo(() => computeMenuDiff(publishedMenuData, menuData), [publishedMenuData, menuData]);
  const hasChanges = diff.totalChanges > 0;

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 20 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 10 }}>
            <BookIcon size={12} /> {t('menu_manager.eyebrow')}
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 6px' }}>
            {t('menu_manager.title_l1')} <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('menu_manager.title_l2')}</em>.
          </h1>
          <p style={{ maxWidth: 580, margin: '0 auto', fontSize: 14.5, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            {t('menu_manager.subtitle')}
          </p>
        </motion.section>

        {/* Alert */}
        <AnimatePresence>
          {alert && (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{
                padding: '10px 14px', borderRadius: 14, marginBottom: 14,
                background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
                color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                fontSize: 13, fontWeight: 800,
              }}
            >{alert.text}</motion.div>
          )}
        </AnimatePresence>

        {/* Restaurant + overview + save bar */}
        <div className="cwm-sheet" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', minWidth: 220 }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 6 }}>RESTAURANT</div>
            <SelectPill
              value={selectedRestaurantId}
              onChange={(v) => { setSelectedRestaurantId(v); setMenuData(null); }}
              options={[{ value: '', label: 'Select a restaurant…' }, ...restaurants.map((r) => ({ value: String(r.id), label: r.name }))]}
            />
          </div>

          <div style={{ flex: '1 1 auto', display: 'flex', gap: 12 }}>
            <QualityStat label="Items" value={quality.totalItems} />
            <QualityStat label="Menus" value={quality.menuCount} />
            <QualityStat label="Needs review" value={`${quality.needsReviewPct}%`} tone={quality.needsReviewPct > 20 ? 'amber' : 'green'} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <PublishStatusPill hasChanges={hasChanges} diff={diff} published={!!publishedMenuData} />
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              disabled={!menuData}
              className="cwm-btn-secondary"
              style={{ padding: '10px 16px', fontSize: 13 }}>
              <ImportIcon size={14} /> Import
            </button>
            {hasChanges && publishedMenuData && (
              <button
                type="button"
                onClick={revertToPublished}
                className="cwm-btn-secondary"
                style={{ padding: '10px 14px', fontSize: 12.5, background: 'transparent', color: 'var(--cwm-gray-500)', border: '1.5px solid var(--cwm-gray-200)' }}>
                Discard draft
              </button>
            )}
            <button
              className="cwm-btn"
              onClick={() => setPublishOpen(true)}
              disabled={saving || !menuData || !hasChanges}
              style={{
                opacity: (saving || !menuData || !hasChanges) ? 0.6 : 1,
                cursor: (saving || !menuData || !hasChanges) ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? <SpinnerDots /> : <><UploadIcon size={14} /> {publishedMenuData ? t('menu_manager.publish_n', { n: diff.totalChanges }) : t('menu_manager.publish')}</>}
            </button>
          </div>
        </div>

        {/* Tab pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {TABS_DEF.map((tab) => {
            const on = activeTab === tab.id;
            return (
              <motion.button
                key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 999,
                  background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                  color: on ? '#fff' : 'var(--cwm-blue-deep)',
                  border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
                  cursor: 'pointer',
                  boxShadow: on ? '0 6px 16px rgba(15,127,232,0.3)' : 'none',
                }}
              >
                <TabIcon name={tab.icon} size={14} /> {t(tab.tKey)}
              </motion.button>
            );
          })}
          <a
            href="/unified/qr"
            style={{
              marginLeft: 'auto', padding: '10px 16px', borderRadius: 999,
              background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
              border: '1.5px solid var(--cwm-blue-light)',
              fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <QRDots size={12} /> QR codes
          </a>
          <a
            href="/unified/menu-manager-legacy"
            style={{
              padding: '10px 16px', borderRadius: 999,
              background: 'transparent', color: 'var(--cwm-gray-500)',
              border: '1.5px dashed var(--cwm-gray-300)',
              fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            Advanced <ArrowIcon size={12} />
          </a>
        </div>

        {/* Tab content */}
        {loading ? (
          <LoadingHero />
        ) : !menuData ? (
          <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center', color: 'var(--cwm-gray-500)' }}>
            Select a restaurant above to start editing.
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.28, ease: EASE }}
            >
              {activeTab === 'items' && (
                <ItemsTab
                  menuData={menuData}
                  activeMenu={activeMenu}
                  onMenuChange={setSelectedMenuId}
                  onAddCategory={addCategory}
                  onRenameCategory={renameCategory}
                  onDeleteCategory={deleteCategory}
                  onAddItem={(catId) => setEditItem({ categoryId: catId, item: blankItem(), isNew: true })}
                  onEditItem={(catId, item) => setEditItem({ categoryId: catId, item, isNew: false })}
                  onDeleteItem={deleteItem}
                  onMarkCategoryReviewed={markCategoryReviewed}
                  onMarkMenuReviewed={markMenuReviewed}
                  onUpdateMenuWindow={updateActiveMenuWindow}
                />
              )}
              {activeTab === 'specials' && (
                <SpecialsTab
                  specials={menuData.specials || []}
                  currency={menuData.currency || 'USD'}
                  onAdd={() => setEditSpecial({ special: blankSpecial(), isNew: true })}
                  onEdit={(s) => setEditSpecial({ special: s, isNew: false })}
                  onDelete={deleteSpecial}
                />
              )}
              {activeTab === 'upsell' && (
                <UpsellTab
                  tips={menuData.upsell_tips || []}
                  onAdd={() => setEditTip({ tip: blankTip(), isNew: true })}
                  onEdit={(t) => setEditTip({ tip: t, isNew: false })}
                  onDelete={deleteTip}
                  onToggle={toggleTipEnabled}
                />
              )}
              {activeTab === 'profile' && restaurant && (
                <ProfileTab
                  restaurant={restaurant}
                  onSave={updateProfile}
                />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Edit dialogs */}
      <AnimatePresence>
        {editItem && (
          <ItemEditDialog
            {...editItem}
            restaurantId={selectedRestaurantId}
            onClose={() => setEditItem(null)}
            onSave={(item) => { upsertItem(editItem.categoryId, item); setEditItem(null); }}
          />
        )}
        {editSpecial && (
          <SpecialEditDialog
            {...editSpecial}
            currency={menuData?.currency || 'USD'}
            onClose={() => setEditSpecial(null)}
            onSave={(s) => { upsertSpecial(s); setEditSpecial(null); }}
          />
        )}
        {editTip && (
          <TipEditDialog
            {...editTip}
            onClose={() => setEditTip(null)}
            onSave={(t) => { upsertTip(t); setEditTip(null); }}
          />
        )}
        {importOpen && (
          <ImportMenuDialog
            onClose={() => setImportOpen(false)}
            onImport={importFromText}
          />
        )}
        {publishOpen && (
          <PublishDiffDialog
            diff={diff}
            saving={saving}
            firstPublish={!publishedMenuData}
            onClose={() => setPublishOpen(false)}
            onPublish={publish}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// ============ TABS ============

function ItemsTab({ menuData, activeMenu, onMenuChange, onAddCategory, onRenameCategory, onDeleteCategory, onAddItem, onEditItem, onDeleteItem, onMarkCategoryReviewed, onMarkMenuReviewed, onUpdateMenuWindow }) {
  const needsReviewTotal = (activeMenu?.categories || []).reduce((n, c) => n + (c.items || []).filter((i) => i.needs_review).length, 0);
  const [showWindow, setShowWindow] = useState(false);
  if (!activeMenu) return (
    <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 8 }}>No menu yet</div>
      <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)' }}>Use the Advanced view to import your first menu.</div>
    </div>
  );

  return (
    <div>
      {/* Menu switcher */}
      {menuData?.menus?.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }}>
          {menuData.menus.map((m) => {
            const on = m.id === activeMenu.id;
            return (
              <button key={m.id} type="button" onClick={() => onMenuChange(m.id)}
                      style={{
                        padding: '7px 14px', borderRadius: 999,
                        background: on ? 'var(--cwm-blue-pale)' : '#fff',
                        color: 'var(--cwm-blue-deep)',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                        whiteSpace: 'nowrap', cursor: 'pointer',
                      }}>
                {m.name}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>{activeMenu.name}</h2>
          {(activeMenu.start_time || activeMenu.end_time) && !showWindow && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 999,
              background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
              border: '1px solid var(--cwm-blue-light)', fontSize: 11, fontWeight: 800,
            }}>
              <ClockIcon size={10} /> {formatTimeRange(activeMenu.start_time, activeMenu.end_time)}
            </span>
          )}
          {onUpdateMenuWindow && (
            <button type="button" onClick={() => setShowWindow((v) => !v)}
                    style={{
                      padding: '4px 10px', borderRadius: 999,
                      background: 'transparent', color: 'var(--cwm-blue-deep)',
                      border: '1.5px dashed var(--cwm-blue-light)',
                      fontFamily: 'var(--cwm-font)', fontSize: 11, fontWeight: 800,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
              <ClockIcon size={10} /> {showWindow ? 'Hide schedule' : 'Schedule'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {needsReviewTotal > 0 && onMarkMenuReviewed && (
            <button type="button" onClick={() => {
              if (window.confirm(`Mark all ${needsReviewTotal} flagged items in this menu as reviewed?`)) onMarkMenuReviewed();
            }}
              style={{
                padding: '7px 14px', borderRadius: 999,
                background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
                border: '1.5px solid #F2D08A',
                fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <CheckIcon size={12} /> Mark menu reviewed ({needsReviewTotal})
            </button>
          )}
          <button type="button" onClick={onAddCategory} style={addBtnStyle}>
            <PlusIcon size={12} /> Add category
          </button>
        </div>
      </div>

      {showWindow && onUpdateMenuWindow && (
        <div className="cwm-sheet" style={{ padding: 14, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-gray-700)' }}>SERVE</div>
          <TimeInput value={activeMenu.start_time || ''} onChange={(v) => onUpdateMenuWindow({ start_time: v })} />
          <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, fontSize: 12 }}>to</span>
          <TimeInput value={activeMenu.end_time || ''} onChange={(v) => onUpdateMenuWindow({ end_time: v })} />
          <QuickTimeChips onPick={(s, e) => onUpdateMenuWindow({ start_time: s, end_time: e })} />
          {(activeMenu.start_time || activeMenu.end_time) && (
            <button type="button" onClick={() => onUpdateMenuWindow({ start_time: '', end_time: '' })}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'transparent', color: 'var(--cwm-gray-500)',
                      border: '1.5px solid var(--cwm-gray-200)',
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                      cursor: 'pointer',
                    }}>
              Clear
            </button>
          )}
        </div>
      )}

      {(!activeMenu.categories || activeMenu.categories.length === 0) ? (
        <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)', marginBottom: 6 }}>No categories yet</div>
          <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)' }}>Tap "Add category" to start organizing your menu.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {activeMenu.categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              onRename={() => onRenameCategory(category.id)}
              onDelete={() => onDeleteCategory(category.id)}
              onAddItem={() => onAddItem(category.id)}
              onEditItem={(item) => onEditItem(category.id, item)}
              onDeleteItem={(itemId) => onDeleteItem(category.id, itemId)}
              onMarkReviewed={onMarkCategoryReviewed ? () => onMarkCategoryReviewed(category.id) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategorySection({ category, onRename, onDelete, onAddItem, onEditItem, onDeleteItem, onMarkReviewed }) {
  const flagged = (category.items || []).filter((i) => i.needs_review).length;
  return (
    <section className="cwm-sheet" style={{ padding: 0, overflow: 'hidden' }}>
      <header style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--cwm-gray-100)', background: 'linear-gradient(180deg, #fff, var(--cwm-blue-50))' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {category.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--cwm-gray-500)', marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {(category.items || []).length} items{flagged ? <> · <span style={{ color: 'var(--cwm-amber-text)' }}>{flagged} flagged</span></> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {flagged > 0 && onMarkReviewed && (
            <button type="button" onClick={onMarkReviewed} title="Mark all reviewed"
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
                      border: '1.5px solid #F2D08A',
                      fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
              <CheckIcon size={11} /> Reviewed
            </button>
          )}
          <IconBtn aria="Rename" onClick={onRename}><EditIcon size={13} /></IconBtn>
          <IconBtn aria="Delete" onClick={onDelete} tone="red"><TrashIcon size={13} /></IconBtn>
          <button type="button" onClick={onAddItem} style={{
            padding: '6px 12px', borderRadius: 999,
            background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)', color: '#fff',
            border: 'none', fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
            boxShadow: '0 4px 10px rgba(15,127,232,0.3)',
          }}>
            <PlusIcon size={11} /> Add item
          </button>
        </div>
      </header>
      {(category.items && category.items.length > 0) ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {category.items.map((item, i) => (
            <li key={item.id} style={{ borderBottom: i < category.items.length - 1 ? '1px solid var(--cwm-gray-100)' : 'none' }}>
              <ItemRow item={item} onEdit={() => onEditItem(item)} onDelete={() => onDeleteItem(item.id)} />
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13, fontWeight: 600 }}>
          No items yet in this category.
        </div>
      )}
    </section>
  );
}

function ItemRow({ item, onEdit, onDelete }) {
  const photoUrl = item.photo?.dataUrl || item.photo_url || null;
  return (
    <div style={{ padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {photoUrl && (
        <div style={{ flexShrink: 0, width: 56, height: 56, borderRadius: 12, overflow: 'hidden', background: 'var(--cwm-gray-100)', border: '1px solid var(--cwm-gray-200)', position: 'relative' }}>
          <img src={photoUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
          {item.photo?.ai && (
            <span style={{ position: 'absolute', bottom: 2, right: 2, width: 14, height: 14, borderRadius: 999, background: '#7C3AED', color: '#fff', display: 'grid', placeItems: 'center' }}>
              <SparkleIcon size={8} />
            </span>
          )}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)' }}>{item.name}</div>
          {item.needs_review && (
            <span style={{
              padding: '2px 8px', borderRadius: 999,
              background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
              fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Needs review
            </span>
          )}
          {item.available === false && (
            <span style={{
              padding: '2px 8px', borderRadius: 999,
              background: 'var(--cwm-gray-100)', color: 'var(--cwm-gray-500)',
              fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              Unavailable
            </span>
          )}
        </div>
        {item.description && (
          <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.4 }}>
            {item.description}
          </p>
        )}
        {(item.allergens?.length > 0 || item.dietary_tags?.length > 0) && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(item.dietary_tags || []).map((t) => (
              <span key={t} className="cwm-tag" style={{ background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)', border: '1px solid #A6E8C2' }}>
                ✓ {t}
              </span>
            ))}
            {(item.allergens || []).map((a) => (
              <span key={a} className="cwm-tag" style={{ background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)', border: '1px solid #F2D08A' }}>
                ⚠ {a}
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', minWidth: 56, textAlign: 'right' }}>
          {formatItemPrice(item)}
        </div>
        <IconBtn aria="Edit" onClick={onEdit}><EditIcon size={13} /></IconBtn>
        <IconBtn aria="Delete" onClick={onDelete} tone="red"><TrashIcon size={13} /></IconBtn>
      </div>
    </div>
  );
}

function SpecialsTab({ specials, currency, onAdd, onEdit, onDelete }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>Today's Specials</h2>
        <button className="cwm-btn" onClick={onAdd} style={{ padding: '8px 16px' }}><PlusIcon size={12} /> Add special</button>
      </div>
      {specials.length === 0 ? (
        <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13 }}>
          No specials configured.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {specials.map((s) => (
            <div key={s.id} className="cwm-sheet" style={{ padding: 16, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>{s.name}</div>
                {s.description && <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.4 }}>{s.description}</p>}
                {(s.available_days?.length > 0 || s.start_time || s.end_time) && (
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {s.available_days?.length > 0 && (
                      <span>Available: <strong style={{ color: 'var(--cwm-blue-deep)' }}>{s.available_days.join(', ')}</strong></span>
                    )}
                    {(s.start_time || s.end_time) && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 999,
                        background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
                        border: '1px solid #F2D08A', fontSize: 11, fontWeight: 800,
                      }}>
                        <ClockIcon size={10} /> {formatTimeRange(s.start_time, s.end_time)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)' }}>
                  {formatSpecialPrice(s, currency)}
                </div>
                <IconBtn aria="Edit" onClick={() => onEdit(s)}><EditIcon size={13} /></IconBtn>
                <IconBtn aria="Delete" onClick={() => onDelete(s.id)} tone="red"><TrashIcon size={13} /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UpsellTab({ tips, onAdd, onEdit, onDelete, onToggle }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>Upsell tips for servers</h2>
        <button className="cwm-btn" onClick={onAdd} style={{ padding: '8px 16px' }}><PlusIcon size={12} /> Add tip</button>
      </div>
      {tips.length === 0 ? (
        <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13 }}>
          No upsell tips yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {tips.map((t) => (
            <div key={t.id} className="cwm-sheet" style={{ padding: 16, opacity: t.enabled ? 1 : 0.55, transition: 'opacity 140ms', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>{t.title}</div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.4 }}>{t.body}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <ToggleSwitch on={!!t.enabled} onChange={() => onToggle(t.id)} />
                <IconBtn aria="Edit" onClick={() => onEdit(t)}><EditIcon size={13} /></IconBtn>
                <IconBtn aria="Delete" onClick={() => onDelete(t.id)} tone="red"><TrashIcon size={13} /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AMENITY_OPTIONS = [
  { id: 'wifi',         label: 'Free Wi-Fi',            icon: 'wifi' },
  { id: 'parking',      label: 'Parking',               icon: 'parking' },
  { id: 'outdoor',      label: 'Outdoor seating',       icon: 'umbrella' },
  { id: 'reservations', label: 'Reservations',          icon: 'calendar' },
  { id: 'delivery',     label: 'Delivery',              icon: 'truck' },
  { id: 'takeout',      label: 'Takeout',               icon: 'bag' },
  { id: 'wheelchair',   label: 'Wheelchair accessible', icon: 'accessibility' },
  { id: 'dogs',         label: 'Dog-friendly',          icon: 'paw' },
  { id: 'tv',           label: 'TVs / sports',          icon: 'tv' },
  { id: 'live_music',   label: 'Live music',            icon: 'music' },
  { id: 'private',      label: 'Private dining',        icon: 'door' },
  { id: 'kids',         label: 'Kid-friendly',          icon: 'kids' },
];

const DAY_IDS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

function blankHours() {
  return DAY_IDS.reduce((acc, d) => ({ ...acc, [d]: { closed: false, open: '09:00', close: '22:00' } }), {});
}

function ProfileTab({ restaurant, onSave }) {
  const [form, setForm] = useState({
    name:         restaurant.name || '',
    address:      restaurant.address || '',
    phone:        restaurant.phone || '',
    cuisine_type: restaurant.cuisine_type || '',
    description:  restaurant.description || '',
    website:      restaurant.website || '',
    amenities:    Array.isArray(restaurant.amenities) ? restaurant.amenities : [],
    hours:        restaurant.hours_json ? safeParseHours(restaurant.hours_json) : blankHours(),
    cover_photo:  restaurant.photo_url ? { dataUrl: restaurant.photo_url, name: 'current cover', size: 0, type: 'image/jpeg' } : null,
  });
  const dirty = true; // extended shape makes dirty-check nuanced; always allow save

  const toggleAmenity = (id) => setForm((f) => ({
    ...f,
    amenities: f.amenities.includes(id) ? f.amenities.filter((x) => x !== id) : [...f.amenities, id],
  }));

  const setHour = (day, patch) => setForm((f) => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], ...patch } } }));
  const copyMondayToAll = () => setForm((f) => {
    const mon = f.hours.mon;
    return { ...f, hours: DAY_IDS.reduce((acc, d) => ({ ...acc, [d]: { ...mon } }), {}) };
  });

  return (
    <div style={{ display: 'grid', gap: 14 }}>

      {/* BASICS */}
      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow">BASICS</div>
        <h2 style={{ margin: '2px 0 16px', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>Identity &amp; location</h2>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Name"><BrandInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Cuisine"><BrandInput value={form.cuisine_type} onChange={(v) => setForm({ ...form, cuisine_type: v })} placeholder="e.g. Italian" /></Field>
            <Field label="Phone"><BrandInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="(555) 123-4567" type="tel" /></Field>
          </div>
          <Field label="Address"><BrandInput value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="123 Main St, New York, NY" /></Field>
          <Field label="Website"><BrandInput value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://…" /></Field>
          <Field label="Description"><BrandTextArea value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="A sentence or two about your restaurant." rows={3} /></Field>
        </div>
      </div>

      {/* COVER PHOTO */}
      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow">COVER PHOTO</div>
        <h2 style={{ margin: '2px 0 14px', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>What diners see first</h2>
        <PhotoPicker
          photo={form.cover_photo}
          onChange={(p) => setForm({ ...form, cover_photo: p })}
          onError={() => {}}
        />
      </div>

      {/* HOURS */}
      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div className="cwm-eyebrow">HOURS</div>
            <h2 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>When you're open</h2>
          </div>
          <button type="button" onClick={copyMondayToAll}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                    border: '1.5px solid var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
            <RefreshIcon size={11} /> Copy Mon to all
          </button>
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {DAY_IDS.map((d) => {
            const row = form.hours[d];
            return (
              <div key={d} style={{ display: 'grid', gridTemplateColumns: '70px 1fr auto', gap: 10, alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {DAY_LABELS[d]}
                </div>
                {row.closed ? (
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)', padding: '8px 12px', borderRadius: 10, background: 'var(--cwm-gray-50)', border: '1px solid var(--cwm-gray-200)' }}>
                    Closed
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TimeInput value={row.open}  onChange={(v) => setHour(d, { open: v })} />
                    <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, fontSize: 12 }}>to</span>
                    <TimeInput value={row.close} onChange={(v) => setHour(d, { close: v })} />
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, color: 'var(--cwm-gray-700)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={row.closed} onChange={(e) => setHour(d, { closed: e.target.checked })}
                         style={{ width: 14, height: 14, accentColor: 'var(--cwm-blue-deep)' }}/>
                  Closed
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* AMENITIES */}
      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow">AMENITIES</div>
        <h2 style={{ margin: '2px 0 14px', fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)' }}>What you offer</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
          {AMENITY_OPTIONS.map((a) => {
            const on = form.amenities.includes(a.id);
            return (
              <button key={a.id} type="button" onClick={() => toggleAmenity(a.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', borderRadius: 14,
                        background: on ? 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))' : '#fff',
                        color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-200)'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                        cursor: 'pointer', transition: 'all 140ms ease',
                        boxShadow: on ? '0 4px 12px rgba(15,127,232,0.16)' : 'none',
                      }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 10,
                  background: on ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-100)',
                  color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>
                  <AmenityIcon name={a.icon} size={14} />
                </span>
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* SAVE */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="cwm-btn" onClick={() => onSave(form)} disabled={!dirty}
                style={{ opacity: dirty ? 1 : 0.55, cursor: dirty ? 'pointer' : 'not-allowed' }}>
          <SaveIcon size={14} /> Save profile
        </button>
      </div>
    </div>
  );
}

function QuickTimeChips({ onPick }) {
  const WINDOWS = [
    { label: 'Breakfast', s: '07:00', e: '10:30' },
    { label: 'Lunch',     s: '11:00', e: '15:00' },
    { label: 'Happy hr',  s: '15:00', e: '18:00' },
    { label: 'Dinner',    s: '17:00', e: '22:00' },
  ];
  return (
    <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {WINDOWS.map((w) => (
        <button key={w.label} type="button" onClick={() => onPick(w.s, w.e)}
                style={{
                  padding: '6px 10px', borderRadius: 999,
                  background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  fontFamily: 'var(--cwm-font)', fontSize: 11, fontWeight: 800,
                  cursor: 'pointer',
                }}>
          {w.label}
        </button>
      ))}
    </div>
  );
}

function TimeInput({ value, onChange }) {
  return (
    <input type="time" value={value} onChange={(e) => onChange(e.target.value)}
           style={{
             padding: '8px 12px', borderRadius: 10,
             border: '1.5px solid var(--cwm-blue-light)',
             background: '#F7FBFF', color: 'var(--cwm-ink)',
             fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 700,
             outline: 'none',
           }}/>
  );
}

function safeParseHours(raw) {
  if (!raw) return blankHours();
  if (typeof raw === 'object') return { ...blankHours(), ...raw };
  try {
    const parsed = JSON.parse(raw);
    return { ...blankHours(), ...parsed };
  } catch { return blankHours(); }
}

// ============ PHOTO PICKER (shared with AddReview pattern) ============

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMG_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif';

function PhotoPicker({ photo, onChange, onError }) {
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { onError?.('Not an image'); return; }
    if (file.size > MAX_PHOTO_BYTES) { onError?.('Too large (10 MB max)'); return; }
    const reader = new FileReader();
    reader.onload = () => onChange({ dataUrl: reader.result, name: file.name, size: file.size, type: file.type });
    reader.readAsDataURL(file);
  };
  const onPick = (e) => handleFile(e.target.files?.[0]);

  if (photo?.dataUrl) {
    return (
      <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', background: 'var(--cwm-gray-50)' }}>
        <img src={photo.dataUrl} alt={photo.name || ''} style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }}/>
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => inputRef.current?.click()}
                  style={iconPillBtn}>
            <RefreshIcon size={12} /> Replace
          </button>
          <button type="button" onClick={() => onChange(null)}
                  style={{ ...iconPillBtn, background: '#fff', color: 'var(--cwm-red-text)' }}>
            <TrashIcon size={12} /> Remove
          </button>
        </div>
        <input ref={inputRef} type="file" accept={ACCEPTED_IMG_TYPES} onChange={onPick} style={{ display: 'none' }}/>
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      style={{
        padding: '28px 20px', borderRadius: 16,
        background: dragging ? 'var(--cwm-blue-pale)' : '#F7FBFF',
        border: `2px dashed ${dragging ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
        cursor: 'pointer', textAlign: 'center', transition: 'all 160ms ease',
      }}>
      <div style={{ width: 48, height: 48, margin: '0 auto 10px', borderRadius: 14, background: '#fff', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center', border: '1.5px solid var(--cwm-blue-light)' }}>
        <CameraIcon size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>
        {dragging ? 'Drop the photo here' : 'Click to upload or drag a photo'}
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
        JPG, PNG, WebP, HEIC, or GIF — up to 10 MB
      </div>
      <input ref={inputRef} type="file" accept={ACCEPTED_IMG_TYPES} onChange={onPick} style={{ display: 'none' }}/>
    </div>
  );
}

const iconPillBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: 999,
  background: 'rgba(255,255,255,0.95)', color: 'var(--cwm-blue-deep)',
  border: 'none', cursor: 'pointer',
  fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 900,
  boxShadow: '0 4px 10px rgba(10,37,64,0.22)',
  backdropFilter: 'blur(6px)',
};

function CameraIcon({ size = 20 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>; }
function RefreshIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 006.49 5L1 10M3.51 15A9 9 0 0017.51 19L23 14"/></svg>; }

function AmenityIcon({ name, size = 14 }) {
  const paths = {
    wifi:          'M5 12a10 10 0 0114 0M8.5 15.5a6 6 0 017 0M12 19h.01',
    parking:       'M6 4h6a4 4 0 010 8H6zM6 4v16',
    umbrella:      'M12 2v20M2 12a10 10 0 0120 0c-2 0-3-1-5-1s-3 1-5 1-3-1-5-1-3 1-5 1z',
    calendar:      'M3 5h18v16H3zM3 10h18M9 3v4M15 3v4',
    truck:         'M3 8h11v9H3zM14 11h4l3 3v3h-7M6 20a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z',
    bag:           'M6 7h12l1 14H5zM9 7a3 3 0 016 0',
    accessibility: 'M12 2a3 3 0 100 6 3 3 0 000-6zM7 13l3-3h4l3 3M10 10v11M14 10v6l3 3',
    paw:           'M7 4a2 2 0 100 4 2 2 0 000-4zM17 4a2 2 0 100 4 2 2 0 000-4zM5 10a2 2 0 100 4 2 2 0 000-4zM19 10a2 2 0 100 4 2 2 0 000-4zM8 18a5 5 0 018-4c3 0 4 2 4 4s-2 3-4 3H8c-2 0-4-1-4-3s1-4 4-4',
    tv:            'M3 6h18v12H3zM8 22h8',
    music:         'M9 17V5l12-2v12M9 17a3 3 0 11-6 0 3 3 0 016 0zM21 15a3 3 0 11-6 0 3 3 0 016 0z',
    door:          'M6 21V3h12v18M9 12h1',
    kids:          'M10 4a2 2 0 114 0 2 2 0 01-4 0zM6 20l2-6 4-2 4 2 2 6M12 12v8',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.wifi}/></svg>;
}

// ============ DIALOGS ============

function ItemEditDialog({ categoryId, item, isNew, restaurantId, onClose, onSave }) {
  const [draft, setDraft] = useState({ ...item });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const toggleAllergen = (a) => setDraft((d) => ({ ...d, allergens: (d.allergens || []).includes(a) ? d.allergens.filter((x) => x !== a) : [...(d.allergens || []), a] }));
  const toggleDietary  = (t) => setDraft((d) => ({ ...d, dietary_tags: (d.dietary_tags || []).includes(t) ? d.dietary_tags.filter((x) => x !== t) : [...(d.dietary_tags || []), t] }));

  const runAI = async () => {
    if (!draft.name?.trim()) { setAiError('Enter the dish name first.'); return; }
    if (!restaurantId) { setAiError('No restaurant selected.'); return; }
    setAiError(null); setAiBusy(true);
    try {
      const data = await dataService.genImageDish(restaurantId, draft.name.trim());
      const dataUrl = data?.img_base64 || data?.image_url || data?.url || null;
      if (!dataUrl) { setAiError('AI returned no image.'); return; }
      setDraft((d) => ({ ...d, photo: { dataUrl, name: `${d.name}-ai.jpg`, size: 0, type: 'image/jpeg', ai: true } }));
    } catch (e) {
      setAiError(e?.message || 'AI generation failed.');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isNew ? 'Add menu item' : 'Edit menu item'}>
      <Field label="Name">
        <BrandInput value={draft.name || ''} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="e.g. Gluten-free pappardelle" />
      </Field>
      <Field label="Description">
        <BrandTextArea value={draft.description || ''} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="Short description of the dish." rows={2} />
      </Field>

      <Field label="Dish photo">
        <PhotoPicker
          photo={draft.photo}
          onChange={(p) => setDraft({ ...draft, photo: p })}
          onError={(msg) => setAiError(msg)}
        />
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={runAI} disabled={aiBusy || !draft.name?.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 999,
                    background: aiBusy ? 'var(--cwm-gray-100)' : 'linear-gradient(90deg, #A855F7, #7C3AED)',
                    color: aiBusy ? 'var(--cwm-gray-500)' : '#fff',
                    border: 'none',
                    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                    cursor: aiBusy || !draft.name?.trim() ? 'not-allowed' : 'pointer',
                    boxShadow: aiBusy ? 'none' : '0 4px 10px rgba(124,58,237,0.3)',
                    opacity: !draft.name?.trim() ? 0.55 : 1,
                  }}>
            {aiBusy ? <><SpinnerDotsPurple /> Generating…</> : <><SparkleIcon size={12} /> Generate with AI</>}
          </button>
          {draft.photo?.ai && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(168,85,247,0.12)', color: '#7C3AED',
              fontSize: 11, fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <SparkleIcon size={10} /> AI
            </span>
          )}
          {aiError && (
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-red-text)' }}>{aiError}</span>
          )}
        </div>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Price">
          <BrandInput
            value={draft.price ?? ''}
            onChange={(v) => setDraft({ ...draft, price: v === '' ? null : Number(v) })}
            placeholder="e.g. 14.00"
            type="number"
          />
        </Field>
        <Field label="Price type">
          <div style={{ display: 'flex', gap: 6 }}>
            {['FIXED', 'MP'].map((pt) => {
              const on = (draft.price_type || 'FIXED') === pt;
              return (
                <button key={pt} type="button" onClick={() => setDraft({ ...draft, price_type: pt })}
                        style={pillBtn(on)}>
                  {pt === 'MP' ? 'Market price' : 'Fixed'}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <Field label="Allergens">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {ALLERGEN_OPTIONS.map((a) => {
            const on = (draft.allergens || []).includes(a);
            return (
              <button key={a} type="button" onClick={() => toggleAllergen(a)}
                      style={chipBtn(on, 'amber')}>
                {on && '✓ '}{a}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Dietary tags">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {DIETARY_OPTIONS.map((t) => {
            const on = (draft.dietary_tags || []).includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleDietary(t)}
                      style={chipBtn(on, 'green')}>
                {on && '✓ '}{t}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Prep methods">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {PREP_METHODS.map((p) => {
            const on = (draft.prep_methods || []).includes(p);
            return (
              <button key={p} type="button"
                      onClick={() => setDraft((d) => ({
                        ...d,
                        prep_methods: (d.prep_methods || []).includes(p)
                          ? d.prep_methods.filter((x) => x !== p)
                          : [...(d.prep_methods || []), p],
                      }))}
                      style={chipBtn(on, 'blue')}>
                {on && '✓ '}{p}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Removable ingredients (comma-separated)">
        <BrandInput
          value={(draft.removable_ingredients || []).join(', ')}
          onChange={(v) => setDraft({
            ...draft,
            removable_ingredients: v.split(',').map((s) => s.trim()).filter(Boolean),
          })}
          placeholder="e.g. pickles, onions, cilantro"
        />
      </Field>

      <Field label={`Modifier groups${(draft.modifier_groups || []).length ? ` (${draft.modifier_groups.length})` : ''}`}>
        <ModifierGroupsEditor
          groups={draft.modifier_groups || []}
          onChange={(groups) => setDraft({ ...draft, modifier_groups: groups })}
        />
      </Field>

      <div style={{ display: 'flex', gap: 14, marginTop: 10, alignItems: 'center' }}>
        <label style={labelRow}>
          <input type="checkbox" checked={!!draft.needs_review} onChange={(e) => setDraft({ ...draft, needs_review: e.target.checked })} style={checkbox} />
          Needs review
        </label>
        <label style={labelRow}>
          <input type="checkbox" checked={draft.available !== false} onChange={(e) => setDraft({ ...draft, available: e.target.checked })} style={checkbox} />
          Available
        </label>
      </div>
      <DialogFooter onClose={onClose} onSave={() => onSave({ ...draft, id: draft.id || uid('item') })} />
    </Modal>
  );
}

function ModifierGroupsEditor({ groups, onChange }) {
  const addGroup = () => onChange([
    ...groups,
    { id: uid('mg'), name: 'New group', required: false, max_choices: 1, options: [] },
  ]);
  const updateGroup = (gid, patch) => onChange(groups.map((g) => g.id === gid ? { ...g, ...patch } : g));
  const removeGroup = (gid) => onChange(groups.filter((g) => g.id !== gid));
  const addOption = (gid) => updateGroup(gid, {
    options: [...(groups.find((g) => g.id === gid)?.options || []), { id: uid('opt'), name: 'Option', price_adjustment: 0 }],
  });
  const updateOption = (gid, oid, patch) => {
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    updateGroup(gid, { options: (g.options || []).map((o) => o.id === oid ? { ...o, ...patch } : o) });
  };
  const removeOption = (gid, oid) => {
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    updateGroup(gid, { options: (g.options || []).filter((o) => o.id !== oid) });
  };

  if (!groups.length) {
    return (
      <button type="button" onClick={addGroup}
              style={{
                width: '100%',
                padding: '12px 14px', borderRadius: 14,
                background: 'transparent', color: 'var(--cwm-blue-deep)',
                border: '1.5px dashed var(--cwm-blue-light)',
                fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
        <PlusIcon size={12} /> Add a modifier group (e.g. "Pick a side")
      </button>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {groups.map((g) => (
        <div key={g.id} style={{ padding: 12, borderRadius: 14, background: 'var(--cwm-blue-pale)', border: '1px solid var(--cwm-blue-light)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input
              value={g.name || ''}
              onChange={(e) => updateGroup(g.id, { name: e.target.value })}
              placeholder="Group name"
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 10,
                border: '1.5px solid var(--cwm-blue-light)', background: '#fff',
                color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                outline: 'none',
              }}
            />
            <label style={{ ...labelRow, fontSize: 11.5 }}>
              <input type="checkbox" checked={!!g.required} onChange={(e) => updateGroup(g.id, { required: e.target.checked })} style={checkbox} />
              Required
            </label>
            <input
              type="number" min={1} max={10}
              value={g.max_choices ?? 1}
              onChange={(e) => updateGroup(g.id, { max_choices: Number(e.target.value) || 1 })}
              title="Max choices"
              style={{
                width: 54, padding: '8px 10px', borderRadius: 10,
                border: '1.5px solid var(--cwm-blue-light)', background: '#fff',
                color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                outline: 'none',
              }}
            />
            <IconBtn aria="Remove group" onClick={() => removeGroup(g.id)} tone="red"><TrashIcon size={12} /></IconBtn>
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            {(g.options || []).map((o) => (
              <div key={o.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={o.name || ''}
                  onChange={(e) => updateOption(g.id, o.id, { name: e.target.value })}
                  placeholder="Option name"
                  style={{
                    flex: 1, padding: '7px 10px', borderRadius: 8,
                    border: '1px solid rgba(15,127,232,0.2)', background: '#fff',
                    color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 600,
                    outline: 'none',
                  }}
                />
                <input
                  type="number" step={0.5}
                  value={o.price_adjustment ?? 0}
                  onChange={(e) => updateOption(g.id, o.id, { price_adjustment: Number(e.target.value) || 0 })}
                  placeholder="+$0"
                  title="Price adjustment"
                  style={{
                    width: 76, padding: '7px 10px', borderRadius: 8,
                    border: '1px solid rgba(15,127,232,0.2)', background: '#fff',
                    color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 700,
                    outline: 'none',
                  }}
                />
                <IconBtn aria="Remove option" onClick={() => removeOption(g.id, o.id)} tone="red"><TrashIcon size={11} /></IconBtn>
              </div>
            ))}
            <button type="button" onClick={() => addOption(g.id)}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'transparent', color: 'var(--cwm-blue-deep)',
                      border: '1.5px dashed var(--cwm-blue-light)',
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
                    }}>
              <PlusIcon size={11} /> Add option
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={addGroup}
              style={{
                width: '100%',
                padding: '10px 14px', borderRadius: 12,
                background: 'transparent', color: 'var(--cwm-blue-deep)',
                border: '1.5px dashed var(--cwm-blue-light)',
                fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
        <PlusIcon size={11} /> Add another group
      </button>
    </div>
  );
}

function SpecialEditDialog({ special, isNew, currency, onClose, onSave }) {
  const [draft, setDraft] = useState({ ...special });
  const toggleDay = (d) => setDraft((x) => ({ ...x, available_days: (x.available_days || []).includes(d) ? x.available_days.filter((y) => y !== d) : [...(x.available_days || []), d] }));

  return (
    <Modal onClose={onClose} title={isNew ? 'Add special' : 'Edit special'}>
      <Field label="Name"><BrandInput value={draft.name || ''} onChange={(v) => setDraft({ ...draft, name: v })} /></Field>
      <Field label="Description"><BrandTextArea value={draft.description || ''} onChange={(v) => setDraft({ ...draft, description: v })} rows={2} /></Field>
      <Field label="Price">
        <BrandInput
          value={draft.price ?? ''}
          onChange={(v) => setDraft({ ...draft, price: v === '' ? null : Number(v) })}
          placeholder="e.g. 18"
          type="number"
        />
      </Field>
      <Field label="Available days">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {DAYS_OF_WEEK.map((d) => {
            const on = (draft.available_days || []).includes(d);
            return (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                      style={chipBtn(on, 'blue')}>
                {d}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Time window (optional)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <TimeInput value={draft.start_time || ''} onChange={(v) => setDraft({ ...draft, start_time: v })} />
          <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, fontSize: 12 }}>to</span>
          <TimeInput value={draft.end_time || ''} onChange={(v) => setDraft({ ...draft, end_time: v })} />
          {(draft.start_time || draft.end_time) && (
            <button type="button" onClick={() => setDraft({ ...draft, start_time: '', end_time: '' })}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'transparent', color: 'var(--cwm-gray-500)',
                      border: '1.5px solid var(--cwm-gray-200)',
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                      cursor: 'pointer',
                    }}>
              Clear
            </button>
          )}
          <QuickTimeChips onPick={(s, e) => setDraft({ ...draft, start_time: s, end_time: e })} />
        </div>
      </Field>

      <DialogFooter onClose={onClose} onSave={() => onSave({ ...draft, id: draft.id || uid('special') })} />
    </Modal>
  );
}

function TipEditDialog({ tip, isNew, onClose, onSave }) {
  const [draft, setDraft] = useState({ enabled: true, ...tip });
  return (
    <Modal onClose={onClose} title={isNew ? 'Add tip' : 'Edit tip'}>
      <Field label="Title">
        <BrandInput value={draft.title || ''} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="e.g. Pair the risotto" />
      </Field>
      <Field label="Body">
        <BrandTextArea value={draft.body || ''} onChange={(v) => setDraft({ ...draft, body: v })} rows={3} placeholder="When a guest orders the risotto, suggest pairing it with a Nero d'Avola." />
      </Field>
      <label style={{ ...labelRow, marginTop: 6 }}>
        <input type="checkbox" checked={!!draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} style={checkbox} />
        Enabled
      </label>
      <DialogFooter onClose={onClose} onSave={() => onSave({ ...draft, id: draft.id || uid('tip') })} />
    </Modal>
  );
}

// ============ SUB-COMPONENTS ============

function Modal({ children, onClose, title }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20 }}>
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 24, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--cwm-shadow-hero)' }}>
        <div style={{ marginBottom: 16 }}>
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
            <BookIcon size={12} /> MENU MANAGER
          </div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>{title}</h2>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

function DialogFooter({ onClose, onSave, saveLabel = 'Save', disableSave = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
      <button className="cwm-btn-secondary" onClick={onClose}>Cancel</button>
      <button
        className="cwm-btn"
        onClick={onSave}
        disabled={disableSave}
        style={{ opacity: disableSave ? 0.6 : 1, cursor: disableSave ? 'not-allowed' : 'pointer' }}>
        {saveLabel}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}

function BrandInput({ value, onChange, placeholder, type = 'text' }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
    style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: 14, border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF', color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600, outline: 'none' }} />;
}

function BrandTextArea({ value, onChange, placeholder, rows = 3 }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: 14, border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF', color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5, outline: 'none', resize: 'vertical' }} />;
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'block' }}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', padding: '12px 36px 12px 16px', borderRadius: 14, border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF', color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

function QualityStat({ label, value, tone }) {
  const color = tone === 'amber' ? 'var(--cwm-amber-text)' : tone === 'green' ? 'var(--cwm-green-text)' : 'var(--cwm-blue-deep)';
  return (
    <div style={{ padding: '4px 10px', borderLeft: '1.5px solid var(--cwm-gray-100)' }}>
      <div className="cwm-eyebrow" style={{ fontSize: 9.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color, letterSpacing: '-0.01em' }}>{value}</div>
    </div>
  );
}

function ToggleSwitch({ on, onChange }) {
  return (
    <button type="button" onClick={onChange} aria-pressed={on}
      style={{ width: 40, height: 22, borderRadius: 999, padding: 2, border: 'none', cursor: 'pointer',
        background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : 'var(--cwm-gray-200)',
        transition: 'background 160ms ease',
      }}>
      <span style={{ display: 'block', width: 18, height: 18, borderRadius: 999, background: '#fff', transform: `translateX(${on ? 18 : 0}px)`, transition: 'transform 160ms ease', boxShadow: '0 2px 6px rgba(10,37,64,0.2)' }} />
    </button>
  );
}

function IconBtn({ children, onClick, aria, tone = 'blue' }) {
  const color = tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-blue-deep)';
  return (
    <button type="button" onClick={onClick} aria-label={aria}
      style={{ width: 28, height: 28, borderRadius: 999, border: `1px solid ${tone === 'red' ? '#F4B6B6' : 'var(--cwm-blue-light)'}`, background: '#fff', color, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </button>
  );
}

function LoadingHero() {
  // Skeleton preview of the Menu Items layout: 3 category sections, each
  // with 2-3 item rows. Feels like content instead of a blank hole.
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="cwm-sheet" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--cwm-gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton h={16} w={160} />
            <Skeleton h={28} w={90} radius={999} />
          </div>
          <div style={{ padding: 0 }}>
            {[0, 1, 2].map((j) => (
              <div key={j} style={{ padding: '14px 18px', borderBottom: j < 2 ? '1px solid var(--cwm-gray-100)' : 'none' }}>
                <Skeleton h={14} w="45%" mb={6} />
                <SkeletonRow lines={2} h={10} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SpinnerDots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}
function SpinnerDotsPurple() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      <style>{`@keyframes cwmDot { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1); } }`}</style>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-gray-500)', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}
function SparkleIcon({ size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.6 5 5 1.6-5 1.6L12 16l-1.6-4.8-5-1.6 5-1.6zM19 14l0.8 2.4 2.2 0.8-2.2 0.8-0.8 2L18 18.0l-2.2-0.8 2.2-0.8zM5 3l0.5 1.6 1.5 0.5-1.5 0.5L5 7.2 4.5 5.6 3 5.1l1.5-0.5z"/></svg>;
}

// ============ STYLES ============

const addBtnStyle = {
  padding: '7px 14px', borderRadius: 999,
  background: 'transparent', color: 'var(--cwm-blue-deep)',
  border: '1.5px dashed var(--cwm-blue-light)',
  fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};

const labelRow = { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-700)', cursor: 'pointer' };
const checkbox = { width: 16, height: 16, accentColor: '#0F7FE8' };

function pillBtn(on) {
  return {
    padding: '10px 14px', borderRadius: 14,
    background: on ? 'linear-gradient(180deg, #fff, var(--cwm-blue-pale))' : '#fff',
    color: 'var(--cwm-blue-deep)',
    border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
    fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
    cursor: 'pointer', flex: 1,
  };
}
function chipBtn(on, tone = 'blue') {
  const palette = tone === 'amber' ? { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' }
    : tone === 'green' ? { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' }
    : { bg: 'var(--cwm-blue-pale)', fg: 'var(--cwm-blue-deep)', solid: 'var(--cwm-blue-deep)' };
  return {
    padding: '6px 12px', borderRadius: 999,
    background: on ? palette.solid : palette.bg,
    color: on ? '#fff' : palette.fg,
    border: `1.5px solid ${on ? palette.solid : 'transparent'}`,
    fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
    cursor: 'pointer',
  };
}

// ============ HELPERS + ICONS ============

function uid(prefix) { return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`; }
function blankMenuData() { return { version: 2, currency: 'USD', language: 'en', updated_at: new Date().toISOString(), menus: [], specials: [], upsell_tips: [] }; }
function blankItem() {
  return {
    id: '', name: '', description: '',
    price: null, price_type: 'FIXED',
    allergens: [], dietary_tags: [],
    prep_methods: [], modifier_groups: [], removable_ingredients: [],
    needs_review: false, available: true,
    photo: null, // { dataUrl, name, size, type } — populated by upload or AI
  };
}
function blankSpecial() { return { id: '', name: '', description: '', price: null, price_type: 'FIXED', available_days: [], start_time: '', end_time: '' }; }
function blankTip() { return { id: '', title: '', body: '', enabled: true }; }

function formatItemPrice(item) {
  if (item.price_type === 'MP' || item.price == null) return 'MP';
  const n = Number(item.price);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
}
function formatSpecialPrice(special, currency = 'USD') {
  if (special.price_type === 'MP' || special.price == null) return 'MP';
  const n = Number(special.price);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
}

// Diffs a published-vs-draft snapshot of menu_data. Intentionally shallow
// — we care about three things the operator will want to scan before
// hitting Publish: (1) categories added/removed, (2) items added/removed,
// (3) items edited (any field changed). Ignores timestamp-only churn.
function computeMenuDiff(published, draft) {
  const base = { addedCategories: [], removedCategories: [], addedItems: [], removedItems: [], editedItems: [], changedSpecials: 0, changedTips: 0, totalChanges: 0 };
  if (!published && !draft) return base;
  if (!published && draft) {
    // First-time publish — everything in the draft counts as "added".
    const cats = collectCats(draft);
    cats.forEach((c) => {
      base.addedCategories.push({ name: c.name });
      (c.items || []).forEach((i) => base.addedItems.push({ name: i.name, category: c.name }));
    });
    base.changedSpecials = (draft.specials || []).length;
    base.changedTips = (draft.upsell_tips || []).length;
    base.totalChanges = base.addedCategories.length + base.addedItems.length + base.changedSpecials + base.changedTips;
    return base;
  }

  const pubCats = collectCats(published);
  const draftCats = collectCats(draft);
  const pubCatById = Object.fromEntries(pubCats.map((c) => [c.id, c]));
  const draftCatById = Object.fromEntries(draftCats.map((c) => [c.id, c]));

  draftCats.forEach((c) => {
    if (!pubCatById[c.id]) base.addedCategories.push({ name: c.name });
  });
  pubCats.forEach((c) => {
    if (!draftCatById[c.id]) base.removedCategories.push({ name: c.name });
  });

  draftCats.forEach((c) => {
    const pubC = pubCatById[c.id];
    if (!pubC) {
      // Whole category is new — all items are adds
      (c.items || []).forEach((i) => base.addedItems.push({ name: i.name, category: c.name }));
      return;
    }
    const pubItems = Object.fromEntries((pubC.items || []).map((i) => [i.id, i]));
    const draftItems = Object.fromEntries((c.items || []).map((i) => [i.id, i]));
    (c.items || []).forEach((i) => {
      const pubI = pubItems[i.id];
      if (!pubI) base.addedItems.push({ name: i.name, category: c.name });
      else if (!sameItem(pubI, i)) base.editedItems.push({ name: i.name, category: c.name });
    });
    (pubC.items || []).forEach((i) => {
      if (!draftItems[i.id]) base.removedItems.push({ name: i.name, category: pubC.name });
    });
  });

  base.changedSpecials = listChanged(published.specials || [], draft.specials || []);
  base.changedTips = listChanged(published.upsell_tips || [], draft.upsell_tips || []);
  base.totalChanges = base.addedCategories.length + base.removedCategories.length
                    + base.addedItems.length + base.removedItems.length + base.editedItems.length
                    + base.changedSpecials + base.changedTips;
  return base;
}

function collectCats(md) {
  if (!md?.menus) return [];
  return md.menus.flatMap((m) => m.categories || []);
}
function sameItem(a, b) {
  const fields = ['name', 'description', 'price', 'price_type', 'needs_review', 'available', 'photo_url'];
  for (const f of fields) if ((a?.[f] ?? null) !== (b?.[f] ?? null)) return false;
  if ((a.photo?.dataUrl || null) !== (b.photo?.dataUrl || null)) return false;
  if (!sameArr(a.allergens || [], b.allergens || [])) return false;
  if (!sameArr(a.dietary_tags || [], b.dietary_tags || [])) return false;
  if (!sameArr(a.prep_methods || [], b.prep_methods || [])) return false;
  if (!sameArr(a.removable_ingredients || [], b.removable_ingredients || [])) return false;
  if (JSON.stringify(a.modifier_groups || []) !== JSON.stringify(b.modifier_groups || [])) return false;
  return true;
}
function sameArr(a, b) {
  if (a.length !== b.length) return false;
  const aS = [...a].sort(); const bS = [...b].sort();
  for (let i = 0; i < aS.length; i++) if (aS[i] !== bS[i]) return false;
  return true;
}
function listChanged(a, b) {
  // Approximate diff for specials / upsell_tips — count adds + removes +
  // items where JSON differs.
  const aById = Object.fromEntries(a.map((x) => [x.id, x]));
  const bById = Object.fromEntries(b.map((x) => [x.id, x]));
  let n = 0;
  b.forEach((x) => { if (!aById[x.id] || JSON.stringify(aById[x.id]) !== JSON.stringify(x)) n++; });
  a.forEach((x) => { if (!bById[x.id]) n++; });
  return n;
}

function computeQuality(menuData) {
  if (!menuData?.menus) return { totalItems: 0, menuCount: 0, needsReviewPct: 0 };
  let total = 0, needs = 0;
  menuData.menus.forEach((m) => {
    (m.categories || []).forEach((c) => {
      (c.items || []).forEach((i) => {
        total++; if (i.needs_review) needs++;
      });
    });
  });
  return {
    totalItems: total,
    menuCount: menuData.menus.length,
    needsReviewPct: total > 0 ? Math.round((needs / total) * 100) : 0,
  };
}

// Icons ---
function BookIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h10v16H4zM4 4a3 3 0 013 3v10M14 4h6v16h-6M14 4a3 3 0 013 3v10"/></svg>; }
function EditIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18 2l4 4-10 10h-4v-4z"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function SaveIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8"/></svg>; }
function ImportIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>; }
function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function ClockIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>; }

function formatTimeRange(start, end) {
  const fmt = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    if (!Number.isFinite(h)) return t;
    const suffix = h >= 12 ? 'p' : 'a';
    const h12 = ((h + 11) % 12) + 1;
    return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
  };
  if (start && end) return `${fmt(start)}–${fmt(end)}`;
  if (start) return `from ${fmt(start)}`;
  if (end) return `until ${fmt(end)}`;
  return '';
}

// ============ MENU TEXT PARSER ============

// Defensive, tolerant parser. Rules:
//   - Blank lines separate blocks
//   - A block's first line is the CATEGORY heading iff it has no price and
//     is either ALL-CAPS or title-case without a leading bullet
//   - Item lines look like:
//       "Name - $price"  /  "Name — $price"
//       "Name $price"    /  "Name ... $price"
//       "Name - description - $price"
//       "Name" (no price — still valid, imported with price: null)
//   - Lines starting with "#" or "//" are comments/dropped
function parseMenuText(raw) {
  const out = { categories: [], itemCount: 0 };
  if (!raw?.trim()) return out;

  // A heading looks like: short (<=40 chars), starts with uppercase, no
  // em/en-dash / " - " item separators, and doesn't end in a trailing price.
  const looksLikeHeading = (line) => {
    if (!line) return false;
    if (/[—–]|\s-\s/.test(line)) return false;           // has item separator
    if (/\s\$?\d+(\.\d{1,2})?\s*$/.test(line)) return false; // ends in price
    if (line.length > 40) return false;
    return /^[A-Z]/.test(line);
  };

  // Flatten all non-empty, non-comment lines but remember blank-line breaks
  // so we can still reset category if the author did the common "heading /
  // blank line / items" pattern.
  const rawLines = raw.split(/\r?\n/).map((l) => l.trim());
  // Accumulate into { category, items } buckets. Heading lines (anywhere in
  // the file) start a new bucket. Other lines become items in the current
  // bucket. Comment lines (# or //) are dropped.
  let buckets = [];
  let current = { name: 'Uncategorized', items: [] };
  for (const line of rawLines) {
    if (!line || line.startsWith('#') || line.startsWith('//')) continue;
    if (looksLikeHeading(line)) {
      if (current.items.length) buckets.push(current);
      current = { name: line.replace(/[:\-–—]+$/, '').trim(), items: [] };
      continue;
    }
    const parsed = parseItemLine(line);
    if (parsed) current.items.push(parsed);
  }
  if (current.items.length) buckets.push(current);

  // Drop the implicit "Uncategorized" bucket if it's empty
  out.categories = buckets.filter((b) => b.items.length > 0);
  out.itemCount = out.categories.reduce((n, c) => n + c.items.length, 0);
  return out;
}

function parseItemLine(line) {
  if (!line) return null;
  // Strip leading bullet/number markers
  const cleaned = line.replace(/^[-•*\d+.)\s]+/, '').trim();
  if (!cleaned) return null;

  // Extract trailing price if present
  const priceMatch = cleaned.match(/(?:^|\s)\$?(\d+(?:\.\d{1,2})?)\s*$/);
  let price = null;
  let body = cleaned;
  if (priceMatch) {
    price = parseFloat(priceMatch[1]);
    body = cleaned.slice(0, priceMatch.index).replace(/[-–—\s]+$/, '').trim();
  }

  // Split body on em/en-dashes or " - " to separate name from description
  const parts = body.split(/\s+[—–-]\s+/, 2);
  const name = parts[0]?.trim();
  const description = parts[1]?.trim() || '';
  if (!name) return null;
  return { name, description, price };
}

// ============ IMPORT DIALOG ============

function ImportMenuDialog({ onClose, onImport }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const onPreview = () => setPreview(parseMenuText(text));

  const EXAMPLE = `APPETIZERS

Burrata - heirloom tomatoes, basil oil - 16
Crispy Calamari - lemon aioli - 14
Wedge Salad - blue cheese, bacon, chives - 12

MAINS

Grilled Salmon - roasted vegetables - 28
Gluten-Free Pappardelle - wild mushrooms - 26
NY Strip Steak - 48`;

  return (
    <Modal onClose={onClose} title="Import menu from text">
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.5 }}>
        Paste your menu below. Blank lines separate categories. Each item can be
        <span style={{ color: 'var(--cwm-blue-deep)', fontWeight: 800 }}> "Name - description - $price"</span> or simpler.
        Every imported item is flagged <em>needs review</em> so you can double-check tags and prices before publish.
      </p>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setPreview(null); }}
        rows={10}
        placeholder={EXAMPLE}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '12px 14px', borderRadius: 14,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#F7FBFF', color: 'var(--cwm-ink)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 12.5, fontWeight: 500, lineHeight: 1.55,
          outline: 'none', resize: 'vertical', minHeight: 180,
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={() => { setText(EXAMPLE); setPreview(null); }}
                style={{
                  padding: '8px 14px', borderRadius: 999,
                  background: '#fff', color: 'var(--cwm-blue-deep)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                  cursor: 'pointer',
                }}>
          Load example
        </button>
        <button type="button" onClick={onPreview} disabled={!text.trim()}
                style={{
                  padding: '8px 14px', borderRadius: 999,
                  background: text.trim() ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-100)',
                  color: text.trim() ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                  border: `1.5px solid ${text.trim() ? 'var(--cwm-blue-light)' : 'var(--cwm-gray-200)'}`,
                  fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                  cursor: text.trim() ? 'pointer' : 'not-allowed',
                }}>
          Preview parse
        </button>
      </div>

      {preview && (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: 'var(--cwm-green-light)', border: '1px solid #A6E8C2' }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)', marginBottom: 8 }}>
            PREVIEW — {preview.categories.length} categor{preview.categories.length === 1 ? 'y' : 'ies'}, {preview.itemCount} items
          </div>
          <div style={{ display: 'grid', gap: 10, maxHeight: 200, overflowY: 'auto' }}>
            {preview.categories.map((c, i) => (
              <div key={i}>
                <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--cwm-green-text)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>
                  {c.name} <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>({c.items.length})</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--cwm-ink)', fontWeight: 600, lineHeight: 1.5 }}>
                  {c.items.map((it, j) => (
                    <li key={j}>
                      {it.name}
                      {it.price != null && <span style={{ color: 'var(--cwm-green-text)', marginLeft: 6 }}>${it.price.toFixed(2)}</span>}
                      {it.description && <span style={{ color: 'var(--cwm-gray-700)', fontWeight: 500 }}> — {it.description}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogFooter
        onClose={onClose}
        onSave={() => onImport(text)}
        saveLabel={<><ImportIcon size={13} /> Import</>}
        disableSave={!text.trim()}
      />
    </Modal>
  );
}
function ArrowIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
function QRDots({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="14" width="3" height="3"/><rect x="14" y="18" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/></svg>; }
function UploadIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>; }
function DotLive({ size = 8 }) { return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: 'var(--cwm-green)', boxShadow: '0 0 0 3px rgba(22,128,60,0.15)' }}/>; }

function PublishStatusPill({ hasChanges, diff, published }) {
  if (!published) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 999,
        background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
        border: '1px solid #F2D08A',
        fontSize: 11, fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: '#D97706' }}/>
        Not published
      </span>
    );
  }
  if (hasChanges) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 999,
        background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
        border: '1px solid var(--cwm-blue-light)',
        fontSize: 11, fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-blue-deep)' }}/>
        {diff.totalChanges} unpublished
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999,
      background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
      border: '1px solid #A6E8C2',
      fontSize: 11, fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>
      <DotLive size={6} />
      Live
    </span>
  );
}

function PublishDiffDialog({ diff, saving, firstPublish, onClose, onPublish }) {
  const groups = [
    { id: 'addedCategories', label: 'Categories added',   tone: 'green',  items: diff.addedCategories.map((c) => c.name) },
    { id: 'removedCategories', label: 'Categories removed', tone: 'red',    items: diff.removedCategories.map((c) => c.name) },
    { id: 'addedItems',      label: 'Items added',        tone: 'green',  items: diff.addedItems.map((i) => `${i.name} · ${i.category}`) },
    { id: 'editedItems',     label: 'Items edited',       tone: 'blue',   items: diff.editedItems.map((i) => `${i.name} · ${i.category}`) },
    { id: 'removedItems',    label: 'Items removed',      tone: 'red',    items: diff.removedItems.map((i) => `${i.name} · ${i.category}`) },
  ];
  const meta = [];
  if (diff.changedSpecials > 0) meta.push(`${diff.changedSpecials} special${diff.changedSpecials === 1 ? '' : 's'} updated`);
  if (diff.changedTips > 0)     meta.push(`${diff.changedTips} upsell tip${diff.changedTips === 1 ? '' : 's'} updated`);

  return (
    <Modal onClose={onClose} title={firstPublish ? 'Publish your menu' : 'Ready to publish?'}>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.55 }}>
        {firstPublish
          ? 'This is your first publish. After you publish, diners see this menu when they scan a QR code, open your Discover card, or chat with your restaurant.'
          : 'Here\'s what\'s changing since your last publish. Diners see the live menu until you hit Publish.'}
      </p>

      <div style={{
        padding: 14, borderRadius: 14, marginBottom: 14,
        background: 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))',
        border: '1.5px solid var(--cwm-blue-light)',
      }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>SUMMARY</div>
        <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em', marginTop: 2 }}>
          {diff.totalChanges} change{diff.totalChanges === 1 ? '' : 's'} to publish
        </div>
        {meta.length > 0 && (
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {meta.join(' · ')}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: 8, maxHeight: 300, overflowY: 'auto', marginBottom: 6 }}>
        {groups.map((g) => {
          if (g.items.length === 0) return null;
          const palette = g.tone === 'green' ? { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', border: '#A6E8C2', sigil: '+' }
                         : g.tone === 'red'  ? { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   border: '#F4B6B6', sigil: '−' }
                         :                     { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  border: 'var(--cwm-blue-light)', sigil: '~' };
          return (
            <div key={g.id} style={{
              padding: '10px 14px', borderRadius: 14,
              background: palette.bg, border: `1.5px solid ${palette.border}`,
            }}>
              <div className="cwm-eyebrow" style={{ color: palette.fg, marginBottom: 6 }}>
                {g.label} · {g.items.length}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
                {g.items.slice(0, 12).map((text, i) => (
                  <li key={i} style={{ fontSize: 12.5, fontWeight: 700, color: palette.fg, display: 'flex', gap: 8 }}>
                    <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', width: 12 }}>{palette.sigil}</span>
                    <span style={{ color: 'var(--cwm-ink)' }}>{text}</span>
                  </li>
                ))}
                {g.items.length > 12 && (
                  <li style={{ fontSize: 11, fontWeight: 800, color: palette.fg, marginLeft: 20 }}>
                    +{g.items.length - 12} more
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button className="cwm-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button
          className="cwm-btn"
          onClick={onPublish}
          disabled={saving || diff.totalChanges === 0}
          style={{ opacity: saving || diff.totalChanges === 0 ? 0.6 : 1, cursor: saving || diff.totalChanges === 0 ? 'not-allowed' : 'pointer' }}>
          {saving ? <SpinnerDots /> : <><UploadIcon size={14} /> Publish {diff.totalChanges > 0 ? `${diff.totalChanges} change${diff.totalChanges === 1 ? '' : 's'}` : 'menu'}</>}
        </button>
      </div>
    </Modal>
  );
}
function TabIcon({ name, size = 14 }) {
  const paths = {
    menu:      'M4 6h16M4 12h16M4 18h10',
    flame:     'M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z',
    lightbulb: 'M9 21h6M10 17h4M12 3a7 7 0 00-4 12c1 1 1 2 1 3h6c0-1 0-2 1-3a7 7 0 00-4-12z',
    store:     'M3 10l1-6h16l1 6M3 10v10h18V10M3 10h18',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.menu}/></svg>;
}
